import React, {
  useState,
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { EstimateContext } from "../Estimate";
import { useEstimateState } from "../EstimateContext";
import { calculateConstructionDesign } from "../../../../../api/projectEstimatesApi";
import "./ProjectTab.css";

const ProjectTab = forwardRef(({ isEditable = true }, ref) => {
  const estimateContext = useContext(EstimateContext);
  const {
    getSectionData,
    performSave,
    estimateData,
    setEstimateData,
    sectionDetails: contextSections,
  } = useEstimateState();

  // Получаем данные управления проектом из нового контекста состояния
  const projectManagementData = useMemo(
    () => getSectionData("project_management") || {},
    [getSectionData]
  );

  // Логируем данные для отладки

  // Функция для извлечения ставок специалистов из API данных
  const getManagementRoleSalariesFromApi = useCallback(() => {
    if (
      projectManagementData.roles &&
      Array.isArray(projectManagementData.roles)
    ) {
      const result = projectManagementData.roles.map((role) => ({
        role: role.role,
        average_salary: role.average_salary,
        coefficient: role.coefficient || 1,
      }));

      return result;
    }

    // Fallback значения по умолчанию - пустой массив
    const fallback = [];
    return fallback;
  }, [projectManagementData]);

  // Получаем данные из API. Порядок по order_index.
  const roles = React.useMemo(() => {
    const fromApi = projectManagementData.roles || [];
    return [...fromApi].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
  }, [projectManagementData.roles]);
  const includeBonuses = projectManagementData.include_bonuses ?? true; // Используем значение из API, по умолчанию true

  const qualityControlPercent = projectManagementData.quality_control_percent;
  const directBonusPercent = projectManagementData.direct_bonus_percent;
  const administrationEffortsInit =
    projectManagementData.administration_efforts ??
    roles.find((r) => r.role === "Администрирование Проекта")?.day_efforts
      ?.direct;

  // Состояния для редактируемых полей
  const [editingFields, setEditingFields] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [localValues, setLocalValues] = useState({
    include_bonuses: includeBonuses,
    quality_control_percent: qualityControlPercent,
    direct_bonus_percent: directBonusPercent,
    administration_efforts: administrationEffortsInit,
    // Индивидуальные ставки для каждого специалиста - используем данные из API
    management_role_salaries: getManagementRoleSalariesFromApi(),
  });

  // Состояние для принудительного обновления отображения
  const [forceUpdate, setForceUpdate] = useState(0);

  // Обновляем локальные значения при изменении данных из контекста
  useEffect(() => {
    // Проверяем, есть ли активные поля в редактировании
    const hasActiveEditing = Object.values(editingFields).some(
      (isEditing) => isEditing
    );
    const hasActiveSaving = Object.values(savingFields).some(
      (isSaving) => isSaving
    );

    if (hasActiveEditing || hasActiveSaving) {
      return;
    }

    // Проверяем, есть ли пользовательские изменения в localValues
    const hasUserChanges =
      localValues.include_bonuses !==
        (projectManagementData.include_bonuses ?? true) ||
      localValues.quality_control_percent !==
        projectManagementData.quality_control_percent ||
      localValues.direct_bonus_percent !==
        projectManagementData.direct_bonus_percent ||
      localValues.administration_efforts !==
        (projectManagementData.administration_efforts ??
          roles.find((r) => r.role === "Администрирование Проекта")?.day_efforts
            ?.direct ??
          9) ||
      JSON.stringify(localValues.management_role_salaries) !==
        JSON.stringify(getManagementRoleSalariesFromApi());

    if (hasUserChanges) {
      return;
    }

    const newValues = {
      include_bonuses: projectManagementData.include_bonuses ?? true,
      quality_control_percent: projectManagementData.quality_control_percent,
      direct_bonus_percent: projectManagementData.direct_bonus_percent,
      administration_efforts:
        projectManagementData.administration_efforts ??
        roles.find((r) => r.role === "Администрирование Проекта")?.day_efforts
          ?.direct,
      management_role_salaries: getManagementRoleSalariesFromApi(),
    };

    const isEqual =
      localValues.include_bonuses === newValues.include_bonuses &&
      localValues.quality_control_percent ===
        newValues.quality_control_percent &&
      localValues.direct_bonus_percent === newValues.direct_bonus_percent &&
      localValues.administration_efforts === newValues.administration_efforts &&
      JSON.stringify(localValues.management_role_salaries) ===
        JSON.stringify(newValues.management_role_salaries);
    if (!isEqual) {
      setLocalValues(newValues);
    }
  }, [
    projectManagementData,
    roles,
    getManagementRoleSalariesFromApi,
    editingFields,
    savingFields,
    forceUpdate,
    localValues.administration_efforts,
    localValues.direct_bonus_percent,
    localValues.include_bonuses,
    localValues.management_role_salaries,
    localValues.quality_control_percent,
  ]);

  // Синхронизируем локальные изменения с контекстом
  useEffect(() => {
    if (!localValues || !estimateData) return;

    const needsUpdate =
      localValues.include_bonuses !==
        (estimateData.project_management?.include_bonuses ?? true) ||
      localValues.quality_control_percent !==
        estimateData.project_management?.quality_control_percent ||
      localValues.direct_bonus_percent !==
        estimateData.project_management?.direct_bonus_percent ||
      localValues.administration_efforts !==
        estimateData.project_management?.administration_efforts ||
      JSON.stringify(localValues.management_role_salaries) !==
        JSON.stringify(
          estimateData.project_management?.roles?.map((role) => ({
            role: role.role,
            average_salary: role.average_salary,
            coefficient: role.coefficient,
          }))
        );

    if (needsUpdate) {
      setEstimateData((prev) => ({
        ...prev,
        project_management: {
          ...prev.project_management,
          include_bonuses: localValues.include_bonuses,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          administration_efforts: localValues.administration_efforts,
          roles:
            localValues.management_role_salaries?.map((role, index) => {
              // Сохраняем результаты калькуляции из estimateData, если они есть
              const existingRole = prev.project_management?.roles?.[index];
              return {
                ...role,
                day_efforts: existingRole?.day_efforts || {
                  direct: 0,
                  bonus: 0,
                },
                money_expenditures: existingRole?.money_expenditures || {
                  direct: 0,
                  bonus: 0,
                },
              };
            }) || [],
        },
      }));
    }
  }, [localValues, setEstimateData, estimateData]); // estimateData добавлен в зависимости

  // Определяем колонки на основе ролей из API
  const manageColumns = roles.map((role) => role.role) || [];

  // Получаем данные для таблицы из контекста (обновленные после калькуляции)
  const getTableData = () => {
    // Используем данные из контекста, если они есть, иначе из API
    const currentRoles = estimateData?.project_management?.roles || roles;

    // Средняя ставка (первая строка) - берем из roles из контекста или API
    const averageSalaryData = currentRoles.map((role) => role.average_salary);

    // Коэффициент (вторая строка) - берем из roles из контекста или API
    const coefficientData = currentRoles.map((role) => role.coefficient);

    // Трудозатраты (третья строка)
    const laborData = currentRoles.map((role) =>
      (role.role === "Администрирование Проекта"
        ? parseFloat(localValues.administration_efforts) || 0
        : role.day_efforts?.direct || 0
      ).toFixed(1)
    );

    const bonusDaysData = currentRoles.map((role) =>
      (role.day_efforts?.bonus || 0).toFixed(1)
    );

    const directCostsData = currentRoles.map(
      (role) => `₸ ${(role.money_expenditures?.direct || 0).toLocaleString()}`
    );

    const bonusMoneyData = currentRoles.map(
      (role) => `₸ ${(role.money_expenditures?.bonus || 0).toLocaleString()}`
    );

    return {
      averageSalary: averageSalaryData,
      coefficient: coefficientData,
      labor: laborData,
      bonusDays: bonusDaysData,
      directCosts: directCostsData,
      bonusMoney: bonusMoneyData,
    };
  };

  const tableData = getTableData();

  // Новая функция для калькуляции данных управления проектом
  const calculateProjectData = async (updatedProjectData) => {
    try {
      // Получаем projectId из контекста или URL
      const projectId = window.location.pathname.split("/").pop();

      // Получаем текущие данные из контекста
      const currentEstimateData = estimateData || {};
      const currentSectionsData = contextSections || {};

      // Создаем обновленные данные для калькуляции
      const updatedEstimateData = {
        ...currentEstimateData,
        project_management: {
          ...currentEstimateData.project_management,
          // Обновляем только базовые параметры, сохраняя результаты расчетов
          include_bonuses: updatedProjectData.include_bonuses,
          quality_control_percent:
            parseFloat(updatedProjectData.quality_control_percent) || 0,
          direct_bonus_percent:
            parseFloat(updatedProjectData.direct_bonus_percent) || 0,
          administration_efforts:
            parseFloat(updatedProjectData.administration_efforts) || 0,
          // Обновляем только ставки в roles, сохраняя остальные данные
          roles:
            currentEstimateData.project_management?.roles?.map(
              (existingRole, index) => {
                const updatedRole =
                  updatedProjectData.management_role_salaries?.[index];
                if (updatedRole) {
                  return {
                    ...existingRole,
                    average_salary: updatedRole.average_salary,
                    coefficient: updatedRole.coefficient,
                  };
                }
                return existingRole;
              }
            ) || [],
        },
      };

      // Обновляем контекст с новыми данными перед калькуляцией
      setEstimateData(updatedEstimateData);

      // Вызываем калькуляцию
      const calculationResult = await calculateConstructionDesign(
        projectId,
        updatedEstimateData,
        currentSectionsData
      );

      // Обновляем данные в контексте
      if (estimateContext && estimateContext.updateHeaderData) {
        estimateContext.updateHeaderData({
          contract_value: calculationResult.contract_value,
          contract_value_before_nds:
            calculationResult.contract_value_before_nds,
          contract_value_construction:
            calculationResult.contract_value_construction,
          contract_value_construction_percent:
            calculationResult.contract_value_construction_percent,
          cost_price: calculationResult.cost_price,
          cost_price_percent: calculationResult.cost_price_percent,
          difference: calculationResult.difference,
          nds_value: calculationResult.nds_value,
          indirect_expenditures: calculationResult.indirect_expenditures,
          indirect_expenditures_percent:
            calculationResult.indirect_expenditures_percent,
          pure_income: calculationResult.pure_income,
          pure_income_percent: calculationResult.pure_income_percent,
        });
      }

      // Обновляем данные в новом контексте состояния
      if (calculationResult && calculationResult.project_management) {
        setEstimateData((prev) => ({
          ...prev,
          project_management: calculationResult.project_management,
        }));

        // Обновляем localValues с новыми данными от сервера
        setLocalValues((prev) => ({
          ...prev,
          include_bonuses: calculationResult.project_management.include_bonuses,
          quality_control_percent:
            calculationResult.project_management.quality_control_percent,
          direct_bonus_percent:
            calculationResult.project_management.direct_bonus_percent,
          // administration_efforts остается из localValues, так как это пользовательский ввод
          management_role_salaries:
            calculationResult.project_management.roles?.map((role) => ({
              role: role.role,
              average_salary: role.average_salary,
              coefficient: role.coefficient,
            })) || [],
        }));
      }

      // Принудительно обновляем отображение
      setForceUpdate((prev) => prev + 1);
    } catch (error) {
      console.error("Ошибка при калькуляции:", error);
    }
  };

  // Обработчики для редактируемых полей
  const handleEditField = (fieldName, value) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: true }));
    setLocalValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Обработчик для изменения чекбокса "Учитывать бонусы при расчете"
  const handleCheckboxChange = async (fieldName, checked) => {
    try {
      setLocalValues((prev) => ({
        ...prev,
        [fieldName]: checked,
      }));

      // Вызываем калькуляцию с обновленными данными
      await calculateProjectData({
        ...localValues,
        [fieldName]: checked,
      });
    } catch (error) {
      console.error("Ошибка при калькуляции:", error);
    }
  };

  // Обработчик для сохранения изменений в полях
  const handleSaveField = async (fieldName, value) => {
    try {
      setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

      // Обновляем значение в локальном состоянии
      setLocalValues((prev) => ({ ...prev, [fieldName]: value }));

      // Подготавливаем данные для калькуляции
      const updatedProjectData = {
        include_bonuses: localValues.include_bonuses,
        quality_control_percent:
          fieldName === "quality_control_percent"
            ? parseFloat(value) || 0
            : localValues.quality_control_percent,
        direct_bonus_percent:
          fieldName === "direct_bonus_percent"
            ? parseFloat(value) || 0
            : localValues.direct_bonus_percent,
        administration_efforts:
          fieldName === "administration_efforts"
            ? parseFloat(value) || 0
            : localValues.administration_efforts,
        management_role_salaries: localValues.management_role_salaries,
      };

      // Вызываем калькуляцию
      await calculateProjectData(updatedProjectData);

      setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    } catch (error) {
      console.error("Ошибка при калькуляции:", error);
    } finally {
      setSavingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  // Обработчик для сохранения изменений в ставке специалиста
  const handleSaveRoleSalary = async (roleIndex, newSalary) => {
    try {
      setSavingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: true,
      }));

      // Обновляем значение в локальном состоянии
      setLocalValues((prev) => ({
        ...prev,
        management_role_salaries: prev.management_role_salaries.map(
          (role, index) =>
            index === roleIndex
              ? { ...role, average_salary: parseFloat(newSalary) }
              : role
        ),
      }));

      // Подготавливаем данные для калькуляции
      const updatedProjectData = {
        include_bonuses: localValues.include_bonuses,
        quality_control_percent:
          parseFloat(localValues.quality_control_percent) || 0,
        direct_bonus_percent: parseFloat(localValues.direct_bonus_percent) || 0,
        administration_efforts:
          parseFloat(localValues.administration_efforts) || 0,
        management_role_salaries: localValues.management_role_salaries.map(
          (role, index) =>
            index === roleIndex
              ? { ...role, average_salary: parseFloat(newSalary) }
              : role
        ),
      };

      // Вызываем калькуляцию
      await calculateProjectData(updatedProjectData);

      setEditingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    } catch (error) {
      console.error("Ошибка при калькуляции:", error);
    } finally {
      setSavingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    }
  };

  // Обработчик для отмены редактирования ставки специалиста
  const handleCancelRoleSalary = (roleIndex) => {
    setEditingFields((prev) => ({
      ...prev,
      [`role_salary_${roleIndex}`]: false,
    }));
    // Возвращаем исходное значение из API данных
    const originalSalary =
      projectManagementData.roles?.[roleIndex]?.average_salary || 400000;
    setLocalValues((prev) => ({
      ...prev,
      management_role_salaries: prev.management_role_salaries.map(
        (role, index) =>
          index === roleIndex
            ? { ...role, average_salary: originalSalary }
            : role
      ),
    }));
  };

  // Обработчик для сохранения изменений в коэффициенте специалиста
  const handleSaveRoleCoefficient = async (roleIndex, newCoefficient) => {
    try {
      setSavingFields((prev) => ({
        ...prev,
        [`role_coefficient_${roleIndex}`]: true,
      }));

      const parsed = parseFloat(newCoefficient);
      const safeCoefficient = Math.max(0, isNaN(parsed) ? 0 : parsed);

      // Обновляем значение в локальном состоянии
      setLocalValues((prev) => ({
        ...prev,
        management_role_salaries: prev.management_role_salaries.map(
          (role, index) =>
            index === roleIndex
              ? { ...role, coefficient: safeCoefficient }
              : role
        ),
      }));

      // Подготавливаем данные для калькуляции
      const updatedProjectData = {
        include_bonuses: localValues.include_bonuses,
        quality_control_percent:
          parseFloat(localValues.quality_control_percent) || 0,
        direct_bonus_percent: parseFloat(localValues.direct_bonus_percent) || 0,
        administration_efforts:
          parseFloat(localValues.administration_efforts) || 0,
        management_role_salaries: localValues.management_role_salaries.map(
          (role, index) =>
            index === roleIndex
              ? { ...role, coefficient: safeCoefficient }
              : role
        ),
      };

      // Вызываем калькуляцию
      await calculateProjectData(updatedProjectData);

      setEditingFields((prev) => ({
        ...prev,
        [`role_coefficient_${roleIndex}`]: false,
      }));
    } catch (error) {
      console.error("Ошибка при калькуляции:", error);
    } finally {
      setSavingFields((prev) => ({
        ...prev,
        [`role_coefficient_${roleIndex}`]: false,
      }));
    }
  };

  // Обработчик для отмены редактирования коэффициента специалиста
  const handleCancelRoleCoefficient = (roleIndex) => {
    setEditingFields((prev) => ({
      ...prev,
      [`role_coefficient_${roleIndex}`]: false,
    }));
    // Возвращаем исходное значение из API данных
    const originalCoefficient =
      projectManagementData.roles?.[roleIndex]?.coefficient || 1;
    setLocalValues((prev) => ({
      ...prev,
      management_role_salaries: prev.management_role_salaries.map(
        (role, index) =>
          index === roleIndex
            ? { ...role, coefficient: originalCoefficient }
            : role
      ),
    }));
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "quality_control_percent"
          ? qualityControlPercent
          : fieldName === "direct_bonus_percent"
          ? directBonusPercent
          : fieldName === "average_salary_ro43"
          ? roles.find((role) => role.role === "РО 4.3")?.average_salary
          : fieldName === "average_salary_admin"
          ? roles.find((role) => role.role === "Администрирование Проекта")
              ?.average_salary
          : fieldName === "average_salary_gip"
          ? roles.find((role) => role.role === "ГИП-РП")?.average_salary
          : fieldName === "average_salary_assistant"
          ? roles.find((role) => role.role === "Помощник ГИПа")?.average_salary
          : fieldName === "coefficient_ro43"
          ? roles.find((role) => role.role === "РО 4.3")?.coefficient
          : fieldName === "coefficient_admin"
          ? roles.find((role) => role.role === "Администрирование Проекта")
              ?.coefficient
          : fieldName === "coefficient_gip"
          ? roles.find((role) => role.role === "ГИП-РП")?.coefficient
          : fieldName === "coefficient_assistant"
          ? roles.find((role) => role.role === "Помощник ГИПа")?.coefficient
          : fieldName === "admin_efforts"
          ? roles.find((role) => role.role === "Администрирование Проекта")
              ?.day_efforts?.direct
          : fieldName === "administration_efforts"
          ? projectManagementData.administration_efforts ??
            roles.find((r) => r.role === "Администрирование Проекта")
              ?.day_efforts?.direct
          : 0,
    }));
  };

  const handleAddSection = () => {
    // Здесь будет логика добавления нового раздела
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isCurrency = false,
    isPercent = false,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : value;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step={isPercent ? "0.01" : "1"}
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(fieldName, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                onCancel(fieldName);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(fieldName, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = isCurrency
      ? `₸ ${Number(value).toLocaleString()}`
      : isPercent
      ? `${value}`
      : value;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
      >
        <span
          style={{
            fontSize: "16px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
          onClick={() => (!isEditable ? undefined : onEdit(fieldName, value))}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  // Компонент для редактируемой ставки специалиста
  const EditableRoleSalary = ({
    roleIndex,
    role,
    averageSalary,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(averageSalary);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(averageSalary);
    }, [averageSalary]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : averageSalary;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step="1000"
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(roleIndex, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(averageSalary);
                onCancel(roleIndex);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(roleIndex, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = `₸ ${parseFloat(averageSalary).toLocaleString()}`;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
        onClick={() =>
          !isEditable ? undefined : onEdit(roleIndex, averageSalary)
        }
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  // Компонент для редактируемого коэффициента специалиста
  const EditableRoleCoefficient = ({
    roleIndex,
    role,
    coefficient,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(coefficient);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(coefficient);
    }, [coefficient]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : coefficient;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step="0.1"
            min="0"
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(roleIndex, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(coefficient);
                onCancel(roleIndex);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(roleIndex, localValue);
            }}
            disabled={!isEditable || isSaving}
          />
        </div>
      );
    }

    const displayValue = `${parseFloat(coefficient).toFixed(1)}%`;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
        onClick={() =>
          !isEditable ? undefined : onEdit(roleIndex, coefficient)
        }
      >
        <span
          style={{
            fontSize: "14px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !isEditable ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !isEditable ? "default" : "pointer",
          }}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  const manageTable = [
    {
      label: "Трудозатраты (дн)",
      values: tableData.labor,
    },
    {
      label: "Бонусы в днях (дн)",
      values: tableData.bonusDays,
    },
    {
      label: "Прямые затраты (тг)",
      values: tableData.directCosts,
    },
    {
      label: "Бонусы в деньгах (тг)",
      values: tableData.bonusMoney,
    },
  ];

  // Рассчитываем общую сумму из данных API
  const totalDirect = projectManagementData.total_money_expenditures?.direct;
  const totalBonus = projectManagementData.total_money_expenditures?.bonus;
  const total = projectManagementData.total;

  // Экспортируем функции через ref
  useImperativeHandle(ref, () => ({
    getTabData: () => {
      return {
        project_management: {
          include_bonuses: localValues.include_bonuses,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          administration_efforts:
            localValues.administration_efforts != null
              ? Number(localValues.administration_efforts)
              : 0,
          management_role_salaries: localValues.management_role_salaries,
        },
      };
    },
    handleSaveForEstimate: async () => {
      try {
        const projectId = window.location.pathname.split("/").pop();
        await performSave(projectId);
      } catch (error) {
        console.error("Ошибка при сохранении:", error);
        throw error;
      }
    },
  }));

  return (
    <div className="manage-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" и кнопка "Добавить раздел" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={localValues.include_bonuses}
              onChange={(e) =>
                handleCheckboxChange("include_bonuses", e.target.checked)
              }
              disabled={!isEditable}
            />
            <span className="slider"></span>
          </label>
        </div>
        {/* <button
          onClick={handleAddSection}
          disabled={!isEditable}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#007bff",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: !isEditable ? "not-allowed" : "pointer",
            opacity: !isEditable ? 0.6 : 1,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button> */}
      </div>

      {/* Редактируемые поля в одной строке */}
      <div className="manage-title-row">
        <div className="manage-title-block">
          <div className="manage-title">% доля управления проектом</div>
          <EditableField
            fieldName="quality_control_percent"
            value={localValues.quality_control_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.quality_control_percent}
            onEdit={handleEditField}
            isSaving={savingFields.quality_control_percent}
          />
        </div>
        <div className="manage-title-block">
          <div className="manage-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="direct_bonus_percent"
            value={localValues.direct_bonus_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.direct_bonus_percent}
            onEdit={handleEditField}
            isSaving={savingFields.direct_bonus_percent}
          />
        </div>
      </div>

      <div className="manage-table-mock">
        {manageColumns.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th></th>
                {manageColumns.map((col, i) => (
                  <th key={i}>{col}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {/* Строка "Средняя ставка" с индивидуальными значениями */}
              <tr>
                <td style={{ fontWeight: "600", color: "#1a237e" }}>
                  Средняя ставка
                </td>
                {localValues.management_role_salaries.map((role, index) => (
                  <td key={index} style={{ textAlign: "center" }}>
                    <EditableRoleSalary
                      roleIndex={index}
                      role={role.role}
                      averageSalary={role.average_salary}
                      onSave={handleSaveRoleSalary}
                      onCancel={handleCancelRoleSalary}
                      isEditing={editingFields[`role_salary_${index}`]}
                      onEdit={(roleIndex, value) =>
                        setEditingFields((prev) => ({
                          ...prev,
                          [`role_salary_${roleIndex}`]: true,
                        }))
                      }
                      isSaving={savingFields[`role_salary_${index}`]}
                    />
                  </td>
                ))}
              </tr>
              {/* Строка "Коэффициент" с индивидуальными значениями (для Администрирования Проекта не показываем) */}
              <tr>
                <td style={{ fontWeight: "600", color: "#1a237e" }}>
                  Коэффициент
                </td>
                {localValues.management_role_salaries.map((role, index) => (
                  <td key={index} style={{ textAlign: "center" }}>
                    {role.role === "Администрирование Проекта" ? (
                      "—"
                    ) : (
                      <EditableRoleCoefficient
                        roleIndex={index}
                        role={role.role}
                        coefficient={role.coefficient}
                        onSave={handleSaveRoleCoefficient}
                        onCancel={handleCancelRoleCoefficient}
                        isEditing={editingFields[`role_coefficient_${index}`]}
                        onEdit={(roleIndex, value) =>
                          setEditingFields((prev) => ({
                            ...prev,
                            [`role_coefficient_${roleIndex}`]: true,
                          }))
                        }
                        isSaving={savingFields[`role_coefficient_${index}`]}
                      />
                    )}
                  </td>
                ))}
              </tr>
              {/* Остальные строки таблицы */}
              {manageTable.map((row, ridx) => (
                <tr key={ridx}>
                  <td style={{ fontWeight: "500" }}>{row.label}</td>
                  {row.values.map((val, i) => (
                    <td key={i} style={{ textAlign: "center" }}>
                      {row.label === "Трудозатраты (дн)" &&
                      roles[i]?.role === "Администрирование Проекта" ? (
                        <EditableField
                          fieldName="administration_efforts"
                          value={localValues.administration_efforts}
                          isPercent={false}
                          onSave={handleSaveField}
                          onCancel={handleCancelEdit}
                          isEditing={editingFields.administration_efforts}
                          onEdit={() =>
                            handleEditField(
                              "administration_efforts",
                              localValues.administration_efforts
                            )
                          }
                          isSaving={savingFields.administration_efforts}
                        />
                      ) : (
                        val
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: "16px",
            }}
          >
            Загрузка данных...
          </div>
        )}
      </div>
      <div className="manage-table-footer">
        <div className="manage-footer-main">
          <div className="manage-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div className="manage-footer-sum main">
            ₸ {total.toLocaleString()}
          </div>
        </div>
        <div className="manage-footer-right">
          <div className="manage-footer-block">
            <div className="manage-footer-label">Прямые затраты</div>
            <div className="manage-footer-sum">
              ₸ {totalDirect.toLocaleString()}
            </div>
          </div>
          <div className="manage-footer-block">
            <div className="manage-footer-label">Бонусы</div>
            <div className="manage-footer-sum">
              ₸ {totalBonus.toLocaleString()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ProjectTab;
